import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMessageModal } from "../contexts/MessageModalContext";
import AdminDataModal, { FormField } from "../components/AdminDataModal";
import axios from "axios";
import "../styles/adminTopbar.css";
import useAdminAuth from "../hooks/useAdminAuth";
import AdminSidebarMenu from "./AdminSidebarMenu";

const passwordFields: FormField[] = [
  {
    key: "currentPassword",
    label: "Current Password",
    type: "password",
    colSpan: 2,
  },
  {
    key: "newPassword",
    label: "New Password",
    type: "password",
    colSpan: 2,
  },
  {
    key: "confirmPassword",
    label: "Confirm Password",
    type: "password",
    colSpan: 2,
  },
];

export default function Topbar() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;

  const { admin } = useAdminAuth();
  const { showModal } = useMessageModal();

  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    await fetch("/api/admin/logout", {
      method: "POST",
      credentials: "include",
    });

    navigate("/admin/login");
  };

  const handleOpenChangePassword = () => {
    setIsMenuOpen(false);
    setIsPasswordModalOpen(true);
  };

  const handleChangePassword = async (formData: any) => {
    const currentPassword = String(formData.currentPassword || "").trim();
    const newPassword = String(formData.newPassword || "").trim();
    const confirmPassword = String(formData.confirmPassword || "").trim();

    if (!currentPassword || !newPassword || !confirmPassword) {
      showModal({
        type: "warning",
        title: "Missing Information",
        message:
          "Please fill in current password, new password, and confirm password.",
      });
      return;
    }

    if (newPassword.length < 6) {
      showModal({
        type: "warning",
        title: "Weak Password",
        message: "New password must be at least 6 characters long.",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      showModal({
        type: "warning",
        title: "Password Mismatch",
        message: "New Password and Confirm Password are mismatched.",
      });
      return;
    }

    try {
      setIsChangingPassword(true);

      const res = await axios.post(
        `${BASE}/admin/users/change-password`,
        {
          currentPassword,
          newPassword,
          confirmPassword,
        },
        {
          withCredentials: true,
        },
      );

      showModal({
        type: "success",
        title: "Password Updated",
        message:
          res.data?.message || "Your password has been changed successfully.",
      });

      setIsPasswordModalOpen(false);
    } catch (error: any) {
      showModal({
        type: "error",
        title: "Change Password Failed",
        message:
          error?.response?.data?.message ||
          "Failed to change password. Please try again.",
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <>
      <div className="admin-topbar">
        <h1>Admin Dashboard</h1>

        <div className="profile-dropdown" ref={dropdownRef}>
          <span>{admin?.name}</span>

          <button
            type="button"
            className="topbar-burger-btn"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            aria-label="Open admin menu"
          >
            ☰
          </button>

          {/* {isMenuOpen && (
            <div className="topbar-profile-menu">
              <div className="topbar-profile-info">
                <strong>{admin?.name || "Admin User"}</strong>
                <small>{admin?.email || ""}</small>
              </div>

              <button
                type="button"
                className="topbar-menu-item"
                onClick={handleOpenChangePassword}
              >
                Change Password
              </button>

              <button
                type="button"
                className="topbar-menu-item logout"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          )} */}
          {isMenuOpen && (
            <div className="topbar-profile-menu">
              <div className="topbar-profile-info">
                <strong>{admin?.name || "Admin User"}</strong>
                <small>{admin?.email || ""}</small>
              </div>

              <div className="topbar-mobile-menu">
                <AdminSidebarMenu onNavigate={() => setIsMenuOpen(false)} />
              </div>

              <button
                type="button"
                className="topbar-menu-item"
                onClick={handleOpenChangePassword}
              >
                Change Password
              </button>

              <button
                type="button"
                className="topbar-menu-item logout"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      <AdminDataModal
        isOpen={isPasswordModalOpen}
        mode="edit"
        title="Change Password"
        fields={passwordFields}
        rowData={{}}
        onClose={() => {
          if (!isChangingPassword) {
            setIsPasswordModalOpen(false);
          }
        }}
        onSubmit={handleChangePassword}
        size="md"
      />
    </>
  );
}
